import { useCreateRoute } from 'components/create-dialog/CreateDialogContext';
import { draftIdIn } from 'components/create-dialog/useCreateDraft';
import { useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import Loader from 'components/Loader';
import { useMemo, useState } from 'react';
import { useGetTeamByIdQuery } from 'store/api/teams';
import { CustomField } from 'types/customFields';
import { EntityType } from 'types/entity';
import { TeamForm } from 'types/teams';
import { useClassifiers } from 'utils/useClassifiers';
import { teamDtoToForm } from './teamFormMapping';
import TeamPageForm from './TeamPageForm';

const USED_CLASSIFIERS = ['TEAM_TYPE', 'BUSINESS_AREA_TYPE'];

/** The add form loaded with what a draft holds, read as the team's edit page reads it. */
const TeamDraftAdd = ({ draftId }: { draftId: number }) => {
    const [draft, setDraft] = useState<{ initialData: TeamForm; customFields: CustomField[] }>();
    const { data: team } = useGetTeamByIdQuery(String(draftId), { refetchOnMountOrArgChange: true });
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: USED_CLASSIFIERS });
    const {
        customFields,
        initialCustomFieldsObject,
        isLoading: isLoadingCustomFieldData,
    } = useCustomFields({ entityType: EntityType.TEAM, fields: team?.customFields });

    // Frozen once loaded: a refetch must not reset what the user is typing.
    if (!draft && team && !isLoadingClassifiers && !isLoadingCustomFieldData) {
        setDraft({ initialData: teamDtoToForm(team, classifiers, initialCustomFieldsObject), customFields });
    }

    return draft ? (
        <TeamPageForm
            type="add"
            initialData={draft.initialData}
            classifiers={classifiers}
            entityCustomFields={draft.customFields}
        />
    ) : (
        <Loader />
    );
};

const TeamBlankAdd = () => {
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: USED_CLASSIFIERS,
    });

    const { initialCustomFieldsObject, isLoading: isLoadingCustomFieldData } = useCustomFields({
        entityType: EntityType.TEAM,
    });

    // Set initial form data
    const initialData = useMemo<TeamForm | undefined>(() => {
        if (isLoadingCustomFieldData || !classifiers) {
            return undefined;
        }
        return {
            name: '',
            abbreviation: '',
            teamType: null,
            isActive: true,
            parentTeam: null,
            teamPracticeAreas: [],
            emailAddress: '',
            readonlyTeamEmployees: [],
            teamOffices: [],
            isParent: false,
            teamBusinessAreas: [],
            dmsTeam: null,
            ...initialCustomFieldsObject,
        };
    }, [classifiers, initialCustomFieldsObject, isLoadingCustomFieldData]);

    if (isLoadingClassifiers || isLoadingCustomFieldData || initialData === undefined) {
        return <Loader />;
    }

    return <TeamPageForm type="add" initialData={initialData} classifiers={classifiers} />;
};

/** The team create; its URL with `?draft=<id>` edits that draft instead of a blank team. */
const TeamPageAdd = () => {
    const draftId = draftIdIn(useCreateRoute().location.search);
    return draftId === null ? <TeamBlankAdd /> : <TeamDraftAdd key={draftId} draftId={draftId} />;
};

export default TeamPageAdd;
